import { useCallback, useMemo, useState } from 'react'

import { WPGravityFormField } from '@/types'

export const SITE_COUNTRY_ADMIN_LABEL = 'sg_site_country'
export const SITE_REGION_ADMIN_LABEL = 'sg_site_region'
export const SITE_PROVINCE_ADMIN_LABEL = 'sg_site_province'

type LocationTree = Record<string, Record<string, string[]>>

const toChoice = (value: string) => ({ value, text: value })

const normalizeLines = (content: string) =>
  content
    .replace(/\\r\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

// Markdown "- Paese / -- Regione / --- Provincia" -> albero annidato
const parseLocationMarkdown = (content: string): LocationTree => {
  const tree: LocationTree = {}
  let currentCountry = ''
  let currentRegion = ''

  normalizeLines(content).forEach((line) => {
    if (/^---\s+/.test(line)) {
      const province = line.replace(/^---\s+/, '').trim()
      if (!province || !currentCountry || !currentRegion) return
      const provinces = tree[currentCountry]?.[currentRegion] || []
      if (!provinces.includes(province)) {
        tree[currentCountry][currentRegion] = [...provinces, province]
      }
      return
    }

    if (/^--\s+/.test(line)) {
      const region = line.replace(/^--\s+/, '').trim()
      if (!region || !currentCountry) return
      if (!tree[currentCountry]) tree[currentCountry] = {}
      if (!tree[currentCountry][region]) tree[currentCountry][region] = []
      currentRegion = region
      return
    }

    if (/^-\s+/.test(line)) {
      const country = line.replace(/^-\s+/, '').trim()
      if (!country) return
      if (!tree[country]) tree[country] = {}
      currentCountry = country
      currentRegion = ''
    }
  })

  return tree
}

const replaceFieldChoices = (
  field: WPGravityFormField,
  values: string[]
): WPGravityFormField => ({
  ...field,
  choices: values.filter(Boolean).map(toChoice)
})

/**
 * Gestisce la select a cascata Paese -> Regione -> Provincia dei form Gravity Forms.
 * `markdown` e' il contenuto di `strings.labels.country_region_province`.
 * Passare `enhancedFields` a `<Form fields={...} />` e `handleFieldChange` +
 * `selectDependencies` alle omonime prop di `<Form />`.
 */
export const useLocationCascadeFields = (
  fields: WPGravityFormField[],
  markdown?: string
) => {
  const [selectedCountry, setSelectedCountry] = useState('')
  const [selectedRegion, setSelectedRegion] = useState('')

  const locationTree = useMemo(() => {
    if (typeof markdown !== 'string' || markdown.trim() === '') return {}
    return parseLocationMarkdown(markdown)
  }, [markdown])

  const enhancedFields = useMemo(() => {
    const hasSelectedCountry = selectedCountry.trim() !== ''
    const hasSelectedRegion = selectedRegion.trim() !== ''
    const countries = Object.keys(locationTree)
    const regions = hasSelectedCountry
      ? Object.keys(locationTree[selectedCountry] || {})
      : []
    const provinces =
      hasSelectedCountry && hasSelectedRegion
        ? locationTree[selectedCountry]?.[selectedRegion] || []
        : []

    return fields.map((field) => {
      if (field.adminLabel === SITE_COUNTRY_ADMIN_LABEL) {
        return replaceFieldChoices(field, countries)
      }
      if (field.adminLabel === SITE_REGION_ADMIN_LABEL) {
        return {
          ...replaceFieldChoices(field, regions),
          visibility:
            hasSelectedCountry && regions.length > 0 ? 'visible' : 'hidden'
        }
      }
      if (field.adminLabel === SITE_PROVINCE_ADMIN_LABEL) {
        return {
          ...replaceFieldChoices(field, provinces),
          visibility:
            hasSelectedCountry && hasSelectedRegion && provinces.length > 0
              ? 'visible'
              : 'hidden'
        }
      }
      return field
    })
  }, [fields, locationTree, selectedCountry, selectedRegion])

  const handleFieldChange = useCallback((adminLabel: string, value: string) => {
    if (adminLabel === SITE_COUNTRY_ADMIN_LABEL) {
      setSelectedCountry(value)
      setSelectedRegion('')
      return
    }
    if (adminLabel === SITE_REGION_ADMIN_LABEL) {
      setSelectedRegion(value)
    }
  }, [])

  const resetSelection = useCallback(() => {
    setSelectedCountry('')
    setSelectedRegion('')
  }, [])

  const selectDependencies = useMemo(
    () => ({
      [SITE_COUNTRY_ADMIN_LABEL]: [
        SITE_REGION_ADMIN_LABEL,
        SITE_PROVINCE_ADMIN_LABEL
      ],
      [SITE_REGION_ADMIN_LABEL]: [SITE_PROVINCE_ADMIN_LABEL]
    }),
    []
  )

  return {
    enhancedFields,
    handleFieldChange,
    selectDependencies,
    resetSelection
  }
}
